import { colors as qColors } from 'quasar'
import type { Context } from 'jexl/Expression'
import type { DtdlProperty } from 'src/models/DtdlProperty'
import type { DtdlComplexSchema, DtdlEnumValue } from 'src/models/Dtdl'
import type { GeoTIFFColorMap } from 'src/models/ViewerDataTwin'
import { getCommentValue } from 'src/utils/comments'
import { isNumeric } from 'src/utils/type-checks'
import { jexlEval } from 'src/utils/jexl'
import { paletteColors, allColors, colorsByHue, ColorsByHueType } from './palette-colors'
import type { ColorsByHueProps } from './palette-colors'

const { hexToRgb } = qColors

export const maxTintsLength = Math.max(...colorsByHue.map((cbh) => cbh.tints.length))
export const maxHueLength = colorsByHue.length
export const maxTintsExpression = '[0-9]|(1[0-9])'
export const maxHueExpression = '[0-9]|([1-9][0-9])'
export const regexColorIndexHue = new RegExp(`^(${maxHueExpression}),#`)
export const regexColorIndexTint = new RegExp(`^#,(${maxTintsExpression})`)
export const regexColorIndex = new RegExp(`^(${maxHueExpression}),(${maxTintsExpression})`)

export const regexHexColor = /^#[0-9A-F]{6}([0-9A-F]{2})?$/i

/**
 * check if value is an index expression and wich type
 * @param value index expression
 * @returns {boolean}
 */
const isIndexExpressionType = (value: string | undefined | null): boolean => {
  return (
    typeof value === 'string' &&
    (regexColorIndexHue.test(value) ||
      regexColorIndexTint.test(value) ||
      regexColorIndex.test(value))
  )
}

export enum IndexExpressionType {
  hue = 'hue',
  tint = 'tint',
  uniform = 'uniform '
}

/**
 * Determines the type of index expression based on the provided value.
 * @param value - The value to be evaluated.
 * @returns The type of index expression, or undefined if the value is not a string or does not match any index expression type.
 */
export const getIndexExpressionType = (
  value: string | undefined | null
): IndexExpressionType | undefined => {
  if (typeof value !== 'string') return undefined
  if (regexColorIndexHue.test(value)) return IndexExpressionType.hue
  if (regexColorIndexTint.test(value)) return IndexExpressionType.tint
  if (regexColorIndex.test(value)) return IndexExpressionType.uniform
  return undefined
}

/**
 * check if value is a hex color
 * @param value hex
 * @returns {boolean}
 */
export const isHexColor = (value: string | undefined | null): boolean => {
  if (typeof value !== 'string') return false
  return regexHexColor.test(value)
}

/**
 * is hue or tint color
 * @param value hue or tint name
 * @returns {boolean}
 */
const isHueOrTintColor = (value: string | undefined | null): boolean => {
  return !!getHueOrTintColor(value)
}

/**
 * get hue or tint color
 * @param value hue or tint name
 * @returns {string | undefined}
 */
const getHueOrTintColor = (value: string | undefined | null): string | undefined => {
  if (typeof value !== 'string') return undefined
  const colorsByHueProps = getColorsByHueProps()
  const hueOrTintColor = colorsByHueProps.find((cbh) => cbh.name === value)
  if (!hueOrTintColor) return undefined
  return hueOrTintColor.value
}

/**
 * get hue tint colors
 * @param value hue name or index
 * @returns {string[] | undefined}
 */
export const getTintColorsByHue = (value: string | undefined | null): string[] | undefined => {
  if (typeof value !== 'string') return undefined
  const colorsByHueProps = getColorsByHueProps()
  if (isHueOrTintColor(value)) {
    const hueOrTintColor = colorsByHueProps
      .filter((cbh) => cbh.type === ColorsByHueType.hue)
      .find((cbh) => cbh.name === value)
    if (hueOrTintColor) {
      return hueOrTintColor.tints?.map((t) => t.value)
    }
  } else if (isIndexExpressionType(value)) {
    const _hue = Number.parseInt(value?.split(',')[0], 10)
    if (isNumeric(_hue)) {
      return colorsByHue[_hue % colorsByHue.length].tints?.map((t) => t.value)
    }
  }
  return undefined
}

/**
 * get unique ColorsByHueProps as a flat list and tints nested in hue
 * @returns {ColorsByHueProps[]}
 */
const getColorsByHueProps = (): ColorsByHueProps[] => {
  const uniqueByProp = (list: ColorsByHueProps[], prop: string) => {
    return list.reduce(
      (acc, d) =>
        acc.some((e) => e[prop as keyof ColorsByHueProps] === d[prop as keyof ColorsByHueProps])
          ? acc
          : acc.concat(d),
      [] as ColorsByHueProps[]
    )
  }
  return uniqueByProp(
    colorsByHue.reduce((acc, cbh, index) => {
      const _tints = cbh.tints.map((cbht, tintIndex) => {
        return {
          name: cbht.name,
          value: cbht.value,
          label: cbht.label,
          optionLabel: `${cbht.percentage}`,
          optionValue: `${cbht.percentage}`,
          type: ColorsByHueType.tint,
          expression: `${index + ',' + tintIndex}`
        }
      })
      acc.push({
        name: cbh.name,
        value: cbh.value,
        label: cbh.label,
        optionLabel: cbh.label,
        optionValue: cbh.name,
        type: ColorsByHueType.hue,
        expression: `${index + ',#'}`,
        tints: _tints
      })
      if (_tints) {
        acc.push(..._tints)
      }
      return acc
    }, [] as ColorsByHueProps[]),
    'name'
  )
}
export const colorsByHueProps = getColorsByHueProps()

/**
 * Get a color from color palette
 * @returns Random color in hex format
 */
export const getRandomColor = (): string => {
  // eslint-disable-next-line sonarjs/pseudo-random
  const randomIndex = Math.floor(Math.random() * allColors.length)
  return allColors[randomIndex]
}

/**
 * Try to retrieve the color from the indexed palette, or by expression (expression can be '1,3')
 * @example
 * // returns #E6C9DB (collaboration25)
 * getColorByExpression('1,#', 0, 3)
 */
export const getColorByExpression = (
  colorExpr?: string,
  i1?: number,
  i2?: number,
  context?: Context,
  fallback?: string
): string | undefined => {
  const _colorExpr = typeof colorExpr === 'string' ? `${colorExpr}`?.trim() : undefined
  // index for hue and tint have been defined
  if (!_colorExpr && i1 !== undefined && i2 !== undefined) return getColorByIndex(i1, i2)
  // If no index and no expression, return random color
  if (!_colorExpr) return getRandomColor()
  // JEXL expression

  if (_colorExpr.startsWith('=') && context)
    return getColorByExpression(
      jexlEval(_colorExpr, context) || getColorByExpression(fallback),
      i1,
      i2
    )
  // hue and tint are fixed in expression
  if (regexColorIndex.test(_colorExpr))
    return getColorByIndex(
      Number.parseInt(_colorExpr.split(',')[0], 10),
      Number.parseInt(_colorExpr.split(',')[1], 10),
      getColorByExpression(fallback)
    )
  // hue is fixed in expression, tint is defined by the provided index
  if (i2 !== undefined && regexColorIndexHue.test(_colorExpr))
    return getColorByIndex(
      Number.parseInt(_colorExpr.split(',')[0], 10),
      i2,
      getColorByExpression(fallback)
    )
  // tint is fixed in expression, hue is defined by the provided index
  if (i1 !== undefined && regexColorIndexTint.test(_colorExpr))
    return getColorByIndex(
      i1,
      Number.parseInt(_colorExpr.split(',')[1], 10),
      getColorByExpression(fallback)
    )
  // color hex value is provided
  if (_colorExpr.startsWith('#')) return _colorExpr
  // Palette ex. 'primary'
  return getPaletteColor(_colorExpr)
}

/** Gets the palette color by providing 2 indexes, used for charts */
export const getColorByIndex = (i1 = 0, i2 = 0, fallback?: string) => {
  const tints = colorsByHue[i1 % colorsByHue.length]?.tints
  return tints?.[i2 % tints.length]?.value || fallback
}

/**
 * Evaluate a color expression, get a color from the palette or just return the color if it's a hex string
 * @param color palette or hex color or expression that evaluates to a color
 * @param context context to evaluarte jexl expression
 * @returns hex color
 */
export const getColor = (colorExpr: string, context?: Context) =>
  getColorByExpression(colorExpr, undefined, undefined, context)

/**
 * Get a HEX color from the palette or just return the color if it's not found
 * @param colorName The color name from the palette
 * @returns hex color
 */
export const getPaletteColor = (colorName: string) =>
  paletteColors[colorName as keyof typeof paletteColors] || colorName

/**
 * Convert hex color to vec4
 */
export const hexToVec4 = (color: string) => {
  const isValid = regexHexColor.test(color)
  if (!isValid) {
    throw new Error(`${color} is not a valid hex color`)
  }
  const colorObj = hexToRgb(color)
  const { r, g, b, a } = colorObj
  const colors = [r, g, b, a || 255]

  return colors.map((c) => c / 255)
}

/**
 * Gets color of selected enum value
 * this method is used in TwinEditProperty to visualize colors of an enum drop down menu
 * @param prop the selected enum value property
 * @param parentProp the parent of the enum value property which is also a property with schema Enum
 * @returns The hex color
 */
export const getColorByEnumValue = (
  prop: DtdlEnumValue | undefined,
  parentProp: DtdlProperty
): string | undefined => {
  let colorExpr: string | null = null
  if (
    !parentProp ||
    (parentProp?.name && (parentProp.schema as DtdlComplexSchema)['@type'] !== 'Enum')
  )
    return
  if (!prop) return
  if (prop?.comment) colorExpr = getCommentValue(prop.comment, 'color') as string
  if (!colorExpr) return
  // if it starts with # it's not an expression so it's probably a color
  if (colorExpr?.startsWith('#')) return colorExpr
  if (colorExpr) {
    const color = jexlEval(colorExpr, { [parentProp.name]: prop.enumValue }) as string
    // The expression should return either a color from the palette or directly an rgb string
    // if it wasn't an expression, then try to use the original value as a color from the palette (color:warning should work as well, sinetad of just color:'warning')
    if (!color || typeof color !== 'string') return getPaletteColor(colorExpr) || undefined
    // if the result starts with # it is a hex color
    if (color?.startsWith('#')) return color
    // else try to get it from the palette (will return #000000 if not found)
    else return getPaletteColor(color)
  }
}

/**
 * Gets contrast color
 * this method will rerurn a color with enough contrast base on the givin color
 * @param color color where contrast function will be based on
 * @param colorOptionLight light color option (on dark)
 * @param colorOptionDark dark color option (on light)
 * @param threshold number between 0-255, the higher the threshold the more chance of getting the light option
 * @returns The hex color
 */
export const getContrastColor = (
  color: string | undefined,
  colorOptionLight: string = 'white',
  colorOptionDark: string = 'black',
  threshold?: number
): string | undefined => {
  if (!color) return
  const _threshold =
    threshold && typeof threshold === 'number' && threshold >= 0 && threshold <= 255
      ? threshold
      : 186
  const _color = color?.startsWith('#') ? color : getPaletteColor(color)
  const colorObj = hexToRgb(_color)
  const { r, g, b } = colorObj
  // white is 255 and black is 0
  // so if colorValue < _threshold => colorOptionLight
  const colorValue = r * 0.299 + g * 0.587 + b * 0.114
  if (colorValue < _threshold) {
    return colorOptionLight?.startsWith('#') ? colorOptionLight : getPaletteColor(colorOptionLight)
  } else {
    return colorOptionDark?.startsWith('#') ? colorOptionDark : getPaletteColor(colorOptionDark)
  }
}

/** One editable colormap stop — the same shape ViewerDataStylingPanel edits. */
export interface ColorStop {
  min: number
  max: number
  r: number
  g: number
  b: number
  a: number
}

type ColorTuple = [number, number, number, number]

/**
 * Convert a hex string to a 0–255 RGBA tuple.
 * Alpha-bearing hex is treated as 0–255 per this module's convention; out-of-range
 * (0–100 scale) alpha values are clamped to 255 (fully opaque).
 */
const hexToRgbTuple = (hex: string): ColorTuple => {
  const { r, g, b, a } = hexToRgb(hex)
  return [r, g, b, a === undefined || a > 1 ? 255 : a]
}

const colorMapObjectToStops = (
  colorMap: Record<string, [number, number, number, number] | string>
): ColorStop[] =>
  Object.entries(colorMap).map(([value, rgba]) => {
    const n = Number(value)
    const [r, g, b, a] = Array.isArray(rgba) ? rgba : hexToRgbTuple(rgba)
    return { min: n, max: n, r, g, b, a: a ?? 255 }
  })

/**
 * Parse any GeoTIFFColorMap into editable stops.
 * Accepts: JSON string `[[[min,max],[r,g,b,a]],…]` or `{ value: [r,g,b,a] }` /
 * `{ value: '#hex' }`, the raw array form, and object forms
 * `{ value: [r,g,b,a] }` / `{ value: '#hex' }`.
 */
export const colorMapToStops = (colorMap?: GeoTIFFColorMap): ColorStop[] => {
  if (!colorMap) return []
  if (typeof colorMap === 'string') {
    const trimmed = colorMap.trim()
    if (!trimmed.startsWith('[') && !trimmed.startsWith('{')) return [] // rio-tiler name → no stops
    try {
      const parsed = JSON.parse(trimmed)
      if (Array.isArray(parsed)) {
        return (parsed as [[number, number], ColorTuple][]).map(([[min, max], [r, g, b, a]]) => ({
          min,
          max,
          r,
          g,
          b,
          a: a ?? 255
        }))
      }
      return colorMapObjectToStops(
        parsed as Record<string, [number, number, number, number] | string>
      )
    } catch {
      return []
    }
  }
  if (Array.isArray(colorMap)) {
    return colorMap.map(([[min, max], [r, g, b, a]]) => ({ min, max, r, g, b, a: a ?? 255 }))
  }
  return colorMapObjectToStops(colorMap)
}

/** Serialize stops back to the `[[[min,max],[r,g,b,a]],…]` JSON string the twins store. */
export const stopsToColorMap = (stops: ColorStop[]): string | undefined => {
  if (stops.length === 0) return undefined
  return JSON.stringify(
    stops.map((s) => [
      [s.min, s.max],
      [s.r, s.g, s.b, s.a]
    ])
  )
}

/** Legend output types. */
export interface LegendStop {
  /** Data range of this bucket. min/max are numbers in data units. */
  min: number
  max: number
  /** CSS color for the swatch (rgb()/rgba()). */
  color: string
}

export interface LegendSpec {
  /** 'buckets' → discrete swatch rows; 'none' → name-only row. */
  kind: 'buckets' | 'none'
  stops: LegendStop[]
  /** Optional display unit, e.g. 'm' for depth. */
  unit?: string
  /** Source colormap name when stops could not be resolved (e.g. rio-tiler name). */
  name?: string
}

const rgbaToCss = ([r, g, b, a]: ColorTuple): string =>
  a === undefined || a === 255 ? `rgb(${r},${g},${b})` : `rgba(${r},${g},${b},${a / 255})`

/** Build a LegendSpec from a twin's colorMap/colorMapName. */
export const parseColorMap = (colorMap?: GeoTIFFColorMap, colorMapName?: string): LegendSpec => {
  const stops = colorMapToStops(colorMap)
  if (stops.length > 0) {
    return {
      kind: 'buckets',
      stops: stops.map((s) => ({ min: s.min, max: s.max, color: rgbaToCss([s.r, s.g, s.b, s.a]) }))
    }
  }
  const name = (typeof colorMap === 'string' ? colorMap : colorMapName)?.trim()
  if (name && COLORMAP_OPTIONS.some((o) => o.value === name.toLowerCase())) {
    return { kind: 'none', stops: [], name }
  }
  return { kind: 'none', stops: [] }
}

/** Canonical rio-tiler colormap list — MOVED here from ViewerDataLinkDialog/colormapOptions.ts. */
export interface ColormapOption {
  label: string
  value: string
}
export const COLORMAP_OPTIONS: ColormapOption[] = [
  { label: 'Blues', value: 'blues' },
  { label: 'Blues (reversed)', value: 'blues_r' },
  { label: 'Greens', value: 'greens' },
  { label: 'Greens (reversed)', value: 'greens_r' },
  { label: 'Reds', value: 'reds' },
  { label: 'Reds (reversed)', value: 'reds_r' },
  { label: 'Oranges', value: 'oranges' },
  { label: 'Oranges (reversed)', value: 'oranges_r' },
  { label: 'Purples', value: 'purples' },
  { label: 'Purples (reversed)', value: 'purples_r' },
  { label: 'Greys', value: 'greys' },
  { label: 'Greys (reversed)', value: 'greys_r' },
  { label: 'Viridis', value: 'viridis' },
  { label: 'Viridis (reversed)', value: 'viridis_r' },
  { label: 'Plasma', value: 'plasma' },
  { label: 'Plasma (reversed)', value: 'plasma_r' },
  { label: 'Inferno', value: 'inferno' },
  { label: 'Inferno (reversed)', value: 'inferno_r' },
  { label: 'Magma', value: 'magma' },
  { label: 'Magma (reversed)', value: 'magma_r' },
  { label: 'Cividis', value: 'cividis' },
  { label: 'Cividis (reversed)', value: 'cividis_r' },
  { label: 'RdYlGn', value: 'rdylgn' },
  { label: 'RdYlGn (reversed)', value: 'rdylgn_r' },
  { label: 'RdYlBu', value: 'rdylbu' },
  { label: 'RdYlBu (reversed)', value: 'rdylbu_r' },
  { label: 'Spectral', value: 'spectral' },
  { label: 'Spectral (reversed)', value: 'spectral_r' },
  { label: 'RdBu', value: 'rdbu' },
  { label: 'RdBu (reversed)', value: 'rdbu_r' },
  { label: 'Terrain', value: 'terrain' },
  { label: 'Terrain (reversed)', value: 'terrain_r' },
  { label: 'Gist Earth', value: 'gist_earth' },
  { label: 'Gist Earth (reversed)', value: 'gist_earth_r' },
  { label: 'Rainbow', value: 'rainbow' },
  { label: 'Rainbow (reversed)', value: 'rainbow_r' },
  { label: 'Jet', value: 'jet' },
  { label: 'Jet (reversed)', value: 'jet_r' },
  { label: 'Hot', value: 'hot' },
  { label: 'Hot (reversed)', value: 'hot_r' },
  { label: 'Cool', value: 'cool' },
  { label: 'Cool (reversed)', value: 'cool_r' },
  { label: 'Schwarzwald (water depth)', value: 'schwarzwald' },
  { label: 'Cfastie', value: 'cfastie' },
  { label: 'Rplumbo', value: 'rplumbo' }
]
